import { useState, useEffect } from 'react'
import { apiClient, User } from '../lib/api'
import { useLiff } from '../hooks/useLiff'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorAlert from '../components/ErrorAlert'

export default function StaffAdmin() {
  const { accessToken, isReady } = useLiff()
  const [user, setUser] = useState<User | null>(null)
  const [staff, setStaff] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState<string | null>(null)
  const [error, setError] = useState<string>('')
  const [userLoading, setUserLoading] = useState(true)
  const [wageUpdates, setWageUpdates] = useState<Record<string, number>>({})

  // ユーザー情報を取得
  useEffect(() => {
    if (isReady && accessToken) {
      apiClient.setAccessToken(accessToken)
      loadUser()
    }
  }, [isReady, accessToken])

  const loadUser = async () => {
    try {
      const response = await apiClient.getMe()
      setUser(response.user)
    } catch (error) {
      console.error('Failed to load user:', error)
      setError('ユーザー情報の取得に失敗しました')
    } finally {
      setUserLoading(false)
    }
  }

  // 管理者チェック
  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('管理者権限が必要です')
      return
    }
    if (user && user.role === 'admin') {
      loadStaff()
    }
  }, [user])

  const loadStaff = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await apiClient.getAllStaff()
      setStaff(response.staff)
      
      // 現在の時給を初期値に設定
      const initialWages = response.staff.reduce((acc: Record<string, number>, s: User) => {
        acc[s.id] = s.hourly_wage
        return acc
      }, {})
      setWageUpdates(initialWages)
    } catch (error) {
      console.error('Failed to load staff:', error)
      setError('スタッフ情報の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleWageChange = (userId: string, wage: number) => {
    setWageUpdates(prev => ({
      ...prev,
      [userId]: wage
    }))
  }

  const handleUpdateWage = async (userId: string) => {
    const newWage = wageUpdates[userId]
    if (newWage === undefined || newWage < 0) {
      alert('正しい時給を入力してください')
      return
    }

    setUpdating(userId)
    try {
      await apiClient.updateStaffWage(userId, newWage)
      
      // スタッフリストを更新
      setStaff(prev => prev.map(s => 
        s.id === userId ? { ...s, hourly_wage: newWage } : s
      ))
      
      alert('時給を更新しました')
    } catch (error) {
      console.error('Failed to update wage:', error)
      alert('時給の更新に失敗しました')
    } finally {
      setUpdating(null)
    }
  }

  const hasChanges = (userId: string) => {
    const staff_member = staff.find(s => s.id === userId)
    return staff_member && wageUpdates[userId] !== staff_member.hourly_wage
  }

  if (!isReady || userLoading) {
    return <LoadingSpinner message="読み込み中..." />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center mb-6">
            <h1 className="text-xl font-bold text-gray-900">👥 スタッフ管理</h1>
            <p className="text-sm text-gray-600 mt-1">
              時給の設定・変更
            </p>
          </div>

          {loading ? (
            <LoadingSpinner message="スタッフ情報を読み込み中..." />
          ) : (
            <div className="space-y-4">
              {staff.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  スタッフが登録されていません
                </div>
              ) : (
                staff.map((staffMember) => (
                  <div key={staffMember.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                            {staffMember.picture_url ? (
                              <img 
                                src={staffMember.picture_url} 
                                alt={staffMember.display_name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-gray-500 text-lg">👤</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {staffMember.display_name}
                          </h3>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              staffMember.role === 'admin' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {staffMember.role === 'admin' ? '管理者' : 'スタッフ'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          <div className="text-sm text-gray-500">現在の時給</div>
                          <div className="font-medium text-gray-900">
                            ¥{staffMember.hourly_wage.toLocaleString()}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <div className="text-sm text-gray-500">¥</div>
                          <input
                            type="number"
                            value={wageUpdates[staffMember.id] || ''}
                            onChange={(e) => handleWageChange(staffMember.id, parseInt(e.target.value) || 0)}
                            className="w-20 p-2 border border-gray-300 rounded-md text-sm text-center"
                            placeholder="時給"
                            min="0"
                          />
                        </div>
                        
                        <button
                          onClick={() => handleUpdateWage(staffMember.id)}
                          disabled={updating === staffMember.id || !hasChanges(staffMember.id)}
                          className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            hasChanges(staffMember.id)
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {updating === staffMember.id ? (
                            <div className="flex items-center space-x-1">
                              <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                              <span>更新中</span>
                            </div>
                          ) : (
                            '更新'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}